
import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button,
  FormGroup,
  Input,
  Label,
  Spinner
} from 'reactstrap';
import { api } from "../../config";

import { ToastContainer } from 'react-toastify';
import { toast } from 'react-toastify';
import { fetchComAuth, fetchComAuthJson } from '../../common/api';

const DashboardConfig = ({ feiraId }) => {
  const [configs, setConfigs] = useState({});
  const [permissoesDisponiveis, setPermissoesDisponiveis] = useState([]);
  const [userTypes] = useState(['ORGANIZACAO', 'COMERCIAL']);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPermissoesDisponiveis();
    if (feiraId) {
      fetchConfigs();
    }
  }, [feiraId]);

  const fetchPermissoesDisponiveis = async () => {
    try {
      const response = await fetchComAuth(`${api.API_URL}/permissao`, { method: 'GET'});

      const data = await response.json();
      if (data.status && data.data) {
        setPermissoesDisponiveis(data.data);
      }
    } catch (error) {
      console.error("Erro ao buscar permissões disponíveis:", error);
    }
  };

  // Busca as configurações atuais da feira
  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const response = await fetchComAuth(`${api.API_URL}/feira/dashboardPerfil/${feiraId}`, { method: 'GET'});
      const data = await response.json();
      
      if (data.status && data.data) {
        // Transforma os dados da API em um formato mais fácil de manipular no front
        const formattedConfigs = {};
        
        data.data.forEach(item => {
          if (!formattedConfigs[item.chave_perfil]) {
            formattedConfigs[item.chave_perfil] = [];
          }
          formattedConfigs[item.chave_perfil].push(item.dashboard_permissao_id);
        });
        
        setConfigs(formattedConfigs);
      }
    } catch (error) {
      console.error("Erro ao buscar configurações:", error);
    } finally {
      setLoading(false);
    }
  };

  // Manipula mudanças nas permissões
  const handleConfigChange = (userType, permissaoId, isChecked) => {
    setConfigs(prev => {
      const newConfigs = { ...prev };
      
      if (!newConfigs[userType]) {
        newConfigs[userType] = [];
      }
      
      if (isChecked) {
        // Adiciona a permissão se não existir
        if (!newConfigs[userType].includes(permissaoId)) {
          newConfigs[userType] = [...newConfigs[userType], permissaoId];
        }
      } else {
        // Remove a permissão se existir
        newConfigs[userType] = newConfigs[userType].filter(id => id !== permissaoId);
      }
      
      return newConfigs;
    });
  };

  // Salva as configurações no backend
  const salvar = async () => {
    if (!feiraId) return;
    
    setSaving(true);
    try {
      // Prepara os dados no formato esperado pelo backend
      const permissoesParaEnviar = [];
      
      Object.entries(configs).forEach(([perfil, permissoesIds]) => {
        permissoesIds.forEach(permissaoId => {
          permissoesParaEnviar.push({
            perfil,
            dashboard_permissao_id: permissaoId
          });
        });
      });
      
      const payload = {
        feira_id: feiraId,
        permissoes: permissoesParaEnviar
      };
      
      const response = await fetchComAuthJson(`${api.API_URL}/dashboardPerfil`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (data.status) {
        toast.success("Configurações salvas com sucesso!");
        fetchConfigs(); // Recarrega as configurações
      } else {
        throw new Error(data.message || 'Erro ao salvar configurações');
      }
    } catch (error) {
      console.error("Erro ao salvar configurações:", error);
      toast.danger(`Erro ao salvar: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Verifica se uma permissão está ativa para um perfil
  const isPermissaoAtiva = (userType, permissaoId) => {
    return configs[userType]?.includes(permissaoId) || false;
  };

  return (
    <Container fluid>
      {loading ? (
        <div className="text-center my-4">
          <Spinner color="primary">Carregando...</Spinner>
        </div>
      ) : (
        <>
          <Row>
            <ToastContainer closeButton={false} />
            {userTypes.map(userType => (
              <Col md={6} key={userType} className="mb-4">
                <Card>
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0 text-white">Configurações para {userType}</h5>
                  </div>
                  <div className="card-body">
                    <Form>
                      {permissoesDisponiveis.map(permissao => (
                        <FormGroup switch key={`${userType}-${permissao.id}`}>
                          <Input
                            type="switch"
                            id={`${userType}-${permissao.id}`}
                            checked={isPermissaoAtiva(userType, permissao.id)}
                            onChange={(e) => handleConfigChange(
                              userType, 
                              permissao.id, 
                              e.target.checked
                            )}
                          />
                          <Label check for={`${userType}-${permissao.id}`}>
                            {permissao.nome || `Permissão ${permissao.id}`}
                          </Label>
                        </FormGroup>
                      ))}
                    </Form>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          <div className="text-end mt-3">
            <Button 
              color="success" 
              onClick={salvar}
              disabled={saving || !feiraId}
            >
              {saving ? (
                <>
                  <Spinner size="sm">Salvando...</Spinner>
                  <span className="ms-2">Salvando...</span>
                </>
              ) : 'Salvar'}
            </Button>
          </div>
        </>
      )}
    </Container>
  );
};

export default DashboardConfig;